import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../Components/Footer';
import Auth from '../Function/Auth';
import Header from '../Components/Header';
import { toast, ToastContainer } from "react-toastify";
import axios from 'axios';
import { canModuleEdit } from '../Function/Permission';

// GPS Coordinates Parser Helper
const parseGpsCoordinates = (locationStr) => {
  if (!locationStr || typeof locationStr !== 'string') return null;
  const str = locationStr.trim();
  if (!str) return null;

  // Format 1: "Latitude:26.704141237585617, Longitude:83.40978255929826"
  const latLngRegex = /Latitude:\s*([-\d.]+),\s*Longitude:\s*([-\d.]+)/i;
  const match = str.match(latLngRegex);
  if (match) {
    const lat = parseFloat(match[1]);
    const lng = parseFloat(match[2]);
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng, formatted: `${lat.toFixed(5)}, ${lng.toFixed(5)}` };
    }
  }

  // Format 2: "28.6139, 77.2090" or "28.6139,77.2090"
  const commaSplit = str.split(',');
  if (commaSplit.length === 2) {
    const lat = parseFloat(commaSplit[0].replace(/[^\d.-]/g, ''));
    const lng = parseFloat(commaSplit[1].replace(/[^\d.-]/g, ''));
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng, formatted: `${lat.toFixed(5)}, ${lng.toFixed(5)}` };
    }
  }

  return null;
};

function ClientDetails() {
  const authToken = sessionStorage.getItem('authToken');
  const rawBaseUrl = process.env.REACT_APP_BASE_API_URL || 'https://api.marclife.io/api/admin';
  const baseurl = rawBaseUrl.trim();

  // Retrieve user_id from sessionStorage, URL query string, or default to 1958
  const queryUserId = new URLSearchParams(window.location.search).get("user_id");
  const storedUserId = sessionStorage.getItem("clientId");
  const userId = storedUserId || queryUserId || "1958";

  // Data States
  const [userData, setUserData] = useState({});
  const [stakes, setStakes] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [withdraws, setWithdraws] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Edit Form States
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editMobile, setEditMobile] = useState("");
  const [editUserStatus, setEditUserStatus] = useState("active");
  const [editUserWithdraw, setEditUserWithdraw] = useState("enable");
  const [editStakeHold, setEditStakeHold] = useState("0");
  const [editUplineIncome, setEditUplineIncome] = useState("enable");
  const [editDownlineIncome, setEditDownlineIncome] = useState("enable");
  const [fundAmount, setFundAmount] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  // GPS Map Viewer Modal State
  const [activeGpsModal, setActiveGpsModal] = useState({
    isOpen: false,
    title: '',
    lat: null,
    lng: null,
    timestamp: '',
    rawLocation: ''
  });

  // Copy to clipboard helper
  const copyToClipboard = (text, label = 'Address') => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard!`);
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const formatShortWallet = (wallet) => {
    if (!wallet) return 'N/A';
    if (wallet.length <= 14) return wallet;
    return `${wallet.slice(0, 8)}...${wallet.slice(-6)}`;
  };

  // Open Google Maps Directions Route (live GPS navigation from current location to target)
  const openDefineRoute = (lat, lng) => {
    if (!lat || !lng) {
      toast.warn("GPS coordinates unavailable for routing");
      return;
    }
    // Google Maps Directions API URL: automatically routes from current user's location to destination
    const routeUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(routeUrl, '_blank', 'noopener,noreferrer');
  };

  // Open Google Maps Search/Location view
  const openLocationOnMap = (lat, lng) => {
    if (!lat || !lng) {
      toast.warn("GPS coordinates unavailable");
      return;
    }
    const mapUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    window.open(mapUrl, '_blank', 'noopener,noreferrer');
  };

  // Open in-app GPS Modal preview
  const handleOpenGpsModal = (title, locationStr, timestamp = '') => {
    const parsed = parseGpsCoordinates(locationStr);
    if (!parsed) {
      toast.info("No GPS coordinates recorded for this action");
      return;
    }
    setActiveGpsModal({
      isOpen: true,
      title,
      lat: parsed.lat,
      lng: parsed.lng,
      timestamp,
      rawLocation: locationStr
    });
  };

  // Fetch client details from API
  const fetchProfileData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${baseurl}/client/details?user_id=${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.status === 429) {
        toast.warn("Rate limit exceeded. Please try again later.");
        setIsLoading(false);
        return;
      }

      if (response.status === 401) {
        console.warn("Unauthenticated admin request for user details.");
        setIsLoading(false);
        return;
      }

      const result = await response.json();

      if (result.status && result.data) {
        setUserData(result.data);
        if (Array.isArray(result.stakes)) setStakes(result.stakes);
        if (Array.isArray(result.deposits)) setDeposits(result.deposits);
        if (Array.isArray(result.withdraws)) setWithdraws(result.withdraws);
      } else {
        toast.error(result.message || "Failed to retrieve user details");
      }
    } catch (error) {
      console.error("Fetch profile error:", error);
      toast.error("Network or server error while loading client details");
    } finally {
      setIsLoading(false);
    }
  }, [authToken, baseurl, userId]);

  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);

  // Sync edit modal with current user data
  const passEditData = () => {
    setEditName(userData.name || "");
    setEditEmail(userData.email || "");
    setEditMobile(userData.mobile || "");
    setEditUserStatus(userData.user_status || "active");
    setEditUserWithdraw(userData.withdraw_status || "enable");
    setEditStakeHold(userData.stake_hold_days || 0);
    setEditUplineIncome(userData.upline_income || "enable");
    setEditDownlineIncome(userData.downline_income || "enable");
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!canModuleEdit('clients')) {
      toast.error("Permission Denied: Your account only has viewer access for clients.");
      return;
    }
    if (!editName) return toast.error("Please enter Name");

    setEditLoading(true);
    const formData = {
      user_id: userData.id,
      name: editName,
      email: editEmail,
      mobile: editMobile,
      user_status: editUserStatus,
      withdraw_status: editUserWithdraw,
      stakeHold: editStakeHold,
      upline_income: editUplineIncome,
      downline_income: editDownlineIncome
    };

    try {
      const response = await fetch(`${baseurl}/user/edit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (data.status) {
        toast.success(data.message || "User profile updated successfully");
        fetchProfileData();
        // Hide Bootstrap modal safely
        const modalEl = document.getElementById('editUserModal');
        if (modalEl && window.bootstrap) {
          const modalInstance = window.bootstrap.Modal.getInstance(modalEl);
          if (modalInstance) modalInstance.hide();
        }
      } else {
        toast.error(data.message || "Update failed");
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Network error while updating user");
    } finally {
      setEditLoading(false);
    }
  };

  // Fund adjust handler (+Add or -Remove funds)
  const handleFundAdjust = async (type) => {
    if (!fundAmount || Number(fundAmount) <= 0) {
      toast.warn("Please enter a valid amount");
      return;
    }

    const payload = {
      user_id: userData.id,
      amount: fundAmount,
      type: type
    };

    try {
      const response = await axios.post(`${baseurl}/fund-adjust`, payload, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
          "Accept": "application/json"
        }
      });

      if (response.data && response.data.status) {
        toast.success(response.data.message || `Funds ${type === 'add' ? 'added' : 'deducted'} successfully`);
        setFundAmount("");
        fetchProfileData();
      } else {
        toast.error(response.data?.message || "Fund adjustment failed");
      }
    } catch (error) {
      console.error("Fund adjustment error:", error);
      toast.error(error.response?.data?.message || "Failed to adjust user funds");
    }
  };

  // Login as user action
  const loginAsUser = async () => {
    try {
      const response = await fetch(`${baseurl}/login-as-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ user_id: userData.id || userId }),
      });

      const data = await response.json();

      if (data.status === true && data.token) {
        window.open(
          `https://user.reliableandsecureinvestments.com/#/login?auth=${data.token}`,
          "_blank"
        );
      } else {
        toast.error(data.message || "Unable to generate user session token");
      }
    } catch (error) {
      console.error("Login as user error:", error);
      toast.error("Network error during login delegation");
    }
  };

  // Parsed registration and login GPS coordinates
  const registerGps = parseGpsCoordinates(userData.location);
  const loginGps = parseGpsCoordinates(userData.login_location);

  return (
    <>
      <Header>
        <Auth />
        <div className="main-content app-content">
          <div className="container-fluid">

            {/* Breadcrumb Header */}
            <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
              <div>
                <div className="d-flex align-items-center gap-2">
                  <Link to="/clients" className="btn btn-sm btn-outline-secondary">
                    <i className="fa fa-arrow-left me-1"></i> Back to Clients
                  </Link>
                  <h4 className="mb-0 text-dark fw-bold">Client Profile & GPS Tracking</h4>
                </div>
                <p className="text-muted mb-0 fs-13 mt-1">
                  Comprehensive audit, live location GPS tracking, trade stakes, and wallet financials for User #{userData.id || userId}.
                </p>
              </div>
              <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                  onClick={fetchProfileData}
                  title="Reload client data"
                >
                  <i className="fa fa-sync-alt"></i> Refresh Data
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-danger d-flex align-items-center gap-1"
                  onClick={loginAsUser}
                  title="Impersonate & login into user portal"
                >
                  <i className="fa fa-sign-in-alt"></i> Login as User
                </button>
              </div>
            </div>

            {/* Top Row: User Identity Card & Quick Stats */}
            <div className="row row-sm">

              {/* Left Column: Client Identity & Security Information */}
              <div className="col-xl-4 col-lg-5 col-12">
                <div className="card border-0 shadow-sm rounded-3 mb-4">
                  <div className="card-body p-4">
                    {isLoading ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading profile...</span>
                        </div>
                      </div>
                    ) : (
                      <div className="main-profile-overview">
                        {/* Profile Header */}
                        <div className="text-center mb-3">
                          <img
                            src={userData.profile_image || 'assets/images/profile/profile1.avif'}
                            alt={userData.name || 'User Avatar'}
                            className="avatar avatar-xxl rounded-circle border border-3 border-light shadow-sm mb-3"
                            style={{ width: '96px', height: '96px', objectFit: 'cover' }}
                            onError={(e) => { e.target.src = 'assets/images/profile/profile1.avif'; }}
                          />
                          <h5 className="fw-bold mb-1 text-dark">{userData.name || 'Unnamed Client'}</h5>
                          <div className="d-flex justify-content-center align-items-center gap-2 mb-2">
                            <span className="badge bg-primary-subtle text-primary border border-primary-subtle">
                              ID: #{userData.id || userData.user_id}
                            </span>
                            <span className={`badge ${userData.user_status === 'active' ? 'bg-success' : 'bg-danger'}`}>
                              {userData.user_status?.toUpperCase() || 'ACTIVE'}
                            </span>
                            <span className="badge bg-warning-subtle text-warning border border-warning-subtle">
                              Rank: {userData.rank || 'STANDARD'}
                            </span>
                          </div>

                          {/* Monospace Wallet Address with Copy */}
                          <div className="p-2 bg-light rounded-2 d-flex align-items-center justify-content-between mb-3 text-start">
                            <div className="overflow-hidden">
                              <span className="text-muted fs-11 d-block text-uppercase fw-semibold">Wallet Address</span>
                              <span className="font-monospace fs-12 text-dark text-truncate d-block" title={userData.wallet}>
                                {formatShortWallet(userData.wallet)}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-0 text-primary ms-2"
                              onClick={() => copyToClipboard(userData.wallet, 'Wallet Address')}
                              title="Copy full wallet address"
                            >
                              <i className="fa fa-copy fs-14"></i>
                            </button>
                          </div>
                        </div>

                        {/* Network Snapshot Badges */}
                        <div className="row text-center g-2 mb-3 pb-3 border-bottom">
                          <div className="col-4">
                            <div className="p-2 border rounded-2 bg-light">
                              <h6 className="fw-bold mb-0 text-dark fs-15">{userData.my_referral || 0}</h6>
                              <span className="text-muted fs-11 text-uppercase">Direct</span>
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="p-2 border rounded-2 bg-light">
                              <h6 className="fw-bold mb-0 text-success fs-15">{userData.my_active || 0}</h6>
                              <span className="text-muted fs-11 text-uppercase">Active</span>
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="p-2 border rounded-2 bg-light">
                              <h6 className="fw-bold mb-0 text-primary fs-15">{userData.total_team || 0}</h6>
                              <span className="text-muted fs-11 text-uppercase">Team</span>
                            </div>
                          </div>
                        </div>

                        {/* Key Account Information List */}
                        <h6 className="fw-bold fs-12 text-uppercase text-muted mb-2">Account Overview</h6>
                        <ul className="list-group list-group-flush fs-13 mb-3">
                          <li className="list-group-item px-0 py-2 d-flex justify-content-between align-items-center">
                            <span className="text-muted"><i className="fa fa-user-friends me-2 text-primary"></i>Sponsor ID</span>
                            <span className="fw-semibold text-dark">#{userData.sponsor_id || 'None'}</span>
                          </li>
                          <li className="list-group-item px-0 py-2 d-flex justify-content-between align-items-center">
                            <span className="text-muted"><i className="fa fa-tag me-2 text-info"></i>Referral Code</span>
                            <span className="fw-bold font-monospace text-dark">{userData.referral_code || 'N/A'}</span>
                          </li>
                          <li className="list-group-item px-0 py-2 d-flex justify-content-between align-items-center">
                            <span className="text-muted"><i className="fa fa-globe me-2 text-success"></i>Country / Base</span>
                            <span className="fw-semibold text-dark">{userData.fix_address || 'India'}</span>
                          </li>
                          <li className="list-group-item px-0 py-2 d-flex justify-content-between align-items-center">
                            <span className="text-muted"><i className="fa fa-calendar-check me-2 text-warning"></i>Registration Date</span>
                            <span className="text-muted fs-12">{userData.date_time || userData.created_at?.slice(0, 19)}</span>
                          </li>
                          <li className="list-group-item px-0 py-2 d-flex justify-content-between align-items-center">
                            <span className="text-muted"><i className="fa fa-money-check-alt me-2 text-danger"></i>Withdraw Status</span>
                            <span className={`badge ${userData.withdraw_status === 'enable' ? 'bg-success' : 'bg-secondary'}`}>
                              {userData.withdraw_status?.toUpperCase() || 'ENABLE'}
                            </span>
                          </li>
                          <li className="list-group-item px-0 py-2 d-flex justify-content-between align-items-center">
                            <span className="text-muted"><i className="fa fa-chart-line me-2 text-purple"></i>Stake Status</span>
                            <span className={`badge ${userData.stake_status === 'active' ? 'bg-success' : 'bg-warning'}`}>
                              {userData.stake_status?.toUpperCase() || 'ACTIVE'}
                            </span>
                          </li>
                        </ul>

                        {/* Edit Details Action Button */}
                        <div className="d-grid gap-2">
                          {canModuleEdit('clients') ? (
                            <button
                              type="button"
                              className="btn btn-primary d-flex align-items-center justify-content-center gap-2"
                              data-bs-toggle="modal"
                              data-bs-target="#editUserModal"
                              onClick={passEditData}
                            >
                              <i className="fa fa-edit"></i> Edit User Profile
                            </button>
                          ) : (
                            <div className="alert alert-secondary py-2 px-3 text-center mb-0 fs-12 border">
                              <i className="fa fa-lock me-1"></i> View Only Mode (Editing Disabled)
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ADVANCED GPS AUDIT & SECURITY TRACKING CARD */}
                <div className="card border-0 shadow-sm rounded-3 mb-4 bg-light-subtle">
                  <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
                    <h6 className="card-title mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                      <i className="fa fa-map-marker-alt text-danger"></i>
                      <span>GPS Access & Live Route</span>
                    </h6>
                    <span className="badge bg-danger-subtle text-danger border border-danger-subtle">
                      Security Log
                    </span>
                  </div>

                  <div className="card-body p-3">
                    {/* 1. Registration GPS Record */}
                    <div className="p-3 border rounded-3 bg-white mb-3 shadow-xs">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-bold text-dark fs-13 d-flex align-items-center gap-1">
                          <i className="fa fa-user-plus text-primary"></i> Registration Location
                        </span>
                        <span className="badge bg-light text-muted border fs-11">
                          {userData.fix_address || 'India'}
                        </span>
                      </div>

                      <div className="d-flex align-items-center gap-2 mb-2 font-monospace fs-12 text-muted">
                        <i className="fa fa-crosshairs text-danger"></i>
                        <span>{registerGps ? registerGps.formatted : (userData.location || '28.6139, 77.2090')}</span>
                      </div>
                      <p className="text-muted small mb-3 fs-11">
                        Recorded at registration: {userData.date_time || '2026-08-08 16:03:46'}
                      </p>

                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger flex-grow-1 d-flex align-items-center justify-content-center gap-1"
                          onClick={() => {
                            if (registerGps) openDefineRoute(registerGps.lat, registerGps.lng);
                            else openDefineRoute(28.6139, 77.2090);
                          }}
                          title="Define route to bring on location via Google Maps directions"
                        >
                          <i className="fa fa-route"></i> Define Route
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center gap-1"
                          onClick={() => handleOpenGpsModal("Registration Location", userData.location || "28.6139, 77.2090", userData.date_time)}
                          title="View on Interactive Map"
                        >
                          <i className="fa fa-map"></i> View Map
                        </button>
                      </div>
                    </div>

                    {/* 2. Last Login GPS Record */}
                    <div className="p-3 border rounded-3 bg-white mb-2 shadow-xs">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-bold text-dark fs-13 d-flex align-items-center gap-1">
                          <i className="fa fa-sign-in-alt text-success"></i> Last Login Location
                        </span>
                        <span className="badge bg-success-subtle text-success border border-success-subtle fs-11">
                          Logins: {userData.login_count || 1}
                        </span>
                      </div>

                      <div className="d-flex align-items-center gap-2 mb-2 font-monospace fs-12 text-muted">
                        <i className="fa fa-crosshairs text-success"></i>
                        <span>{loginGps ? loginGps.formatted : (userData.login_location || '26.7340, 83.3802')}</span>
                      </div>
                      <p className="text-muted small mb-3 fs-11">
                        Last Active: {userData.last_login || '2026-10-05 21:28:14'}
                      </p>

                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-danger flex-grow-1 d-flex align-items-center justify-content-center gap-1"
                          onClick={() => {
                            if (loginGps) openDefineRoute(loginGps.lat, loginGps.lng);
                            else openDefineRoute(26.7340, 83.3802);
                          }}
                          title="Define route to bring on location via Google Maps directions"
                        >
                          <i className="fa fa-directions"></i> Define Route to Login
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center gap-1"
                          onClick={() => handleOpenGpsModal("Last Login Location", userData.login_location || "26.7340, 83.3802", userData.last_login)}
                          title="View on Interactive Map"
                        >
                          <i className="fa fa-map"></i> View Map
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              {/* Right Column: Financial Dashboard, Quick Fund Adjust, and Multi-Tabs */}
              <div className="col-xl-8 col-lg-7 col-12">

                {/* Primary Financial KPI Cards */}
                <div className="row g-3 mb-4">


                  {/* Card 2: Total Staked / Trade */}
                  <div className="col-md-4 col-sm-6 col-12">
                    <div className="card border-0 shadow-sm rounded-3 h-100">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <span className="text-muted fs-11 text-uppercase fw-semibold">Total Trade / Staked</span>
                            <h4 className="fw-bold mb-0 text-dark mt-1">
                              {Number(userData.my_stake || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                            </h4>
                            <span className="text-muted fs-11">Package Cap: {userData.stake_usdt || 125} USDT</span>
                          </div>
                          <div className="p-2 bg-info-subtle text-info rounded-2">
                            <i className="fa fa-layer-group fs-20"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 3: Total Income */}
                  <div className="col-md-4 col-sm-6 col-12">
                    <div className="card border-0 shadow-sm rounded-3 h-100">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <span className="text-muted fs-11 text-uppercase fw-semibold">Total Earned Income</span>
                            <h4 className="fw-bold mb-0 text-success mt-1">
                              {Number(userData.total_income || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                            </h4>
                            <span className="text-muted fs-11">Cap: ${userData.income_cap_usdt || 375}</span>
                          </div>
                          <div className="p-2 bg-success-subtle text-success rounded-2">
                            <i className="fa fa-hand-holding-usd fs-20"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 4: Total Withdrawn */}
                  <div className="col-md-4 col-sm-6 col-12">
                    <div className="card border-0 shadow-sm rounded-3 h-100">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <span className="text-muted fs-11 text-uppercase fw-semibold">Total Withdrawn</span>
                            <h4 className="fw-bold mb-0 text-warning mt-1">
                              {Number(userData.withdraw_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                            </h4>
                            <span className="text-muted fs-11">Last: ${userData.last_withdraw || 0}</span>
                          </div>
                          <div className="p-2 bg-warning-subtle text-warning rounded-2">
                            <i className="fa fa-arrow-circle-up fs-20"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Secondary Income Streams Breakdown */}
                <div className="card border-0 shadow-sm rounded-3 mb-4">
                  <div className="card-header bg-white border-bottom py-3">
                    <h6 className="card-title mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                      <i className="fa fa-coins text-warning"></i>
                      <span>Income Stream Distribution & Business Volume</span>
                    </h6>
                  </div>
                  <div className="card-body p-3">
                    <div className="row g-2 text-center">
                      <div className="col-md-2 col-4">
                        <div className="p-2 border rounded-2 bg-light">
                          <span className="text-muted fs-11 text-uppercase d-block">Stake Income</span>
                          <span className="fw-bold text-dark fs-13">{Number(userData.stake_income || 0).toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="col-md-2 col-4">
                        <div className="p-2 border rounded-2 bg-light">
                          <span className="text-muted fs-11 text-uppercase d-block">Direct Income</span>
                          <span className="fw-bold text-dark fs-13">{Number(userData.direct_income || 0).toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="col-md-2 col-4">
                        <div className="p-2 border rounded-2 bg-light">
                          <span className="text-muted fs-11 text-uppercase d-block">Matching</span>
                          <span className="fw-bold text-dark fs-13">{Number(userData.maching_income || 0).toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="col-md-2 col-4">
                        <div className="p-2 border rounded-2 bg-light">
                          <span className="text-muted fs-11 text-uppercase d-block">Level Income</span>
                          <span className="fw-bold text-dark fs-13">{Number(userData.level_income || 0).toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="col-md-2 col-4">
                        <div className="p-2 border rounded-2 bg-light">
                          <span className="text-muted fs-11 text-uppercase d-block">Unstake Coin</span>
                          <span className="fw-bold text-dark fs-13">{Number(userData.unstake_amount || 0).toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="col-md-2 col-4">
                        <div className="p-2 border rounded-2 bg-light">
                          <span className="text-muted fs-11 text-uppercase d-block">Team Volume</span>
                          <span className="fw-bold text-primary fs-13">{Number(userData.team_business || 0).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Main Tabbed Transactions & History Card */}
                <div className="card border-0 shadow-sm rounded-3 mb-4">
                  <div className="card-header bg-white border-bottom p-0">
                    <ul className="nav nav-tabs card-header-tabs m-0 border-bottom-0" role="tablist">
                      <li className="nav-item">
                        <button
                          className="nav-link active px-4 py-3 fw-semibold border-0 border-bottom border-2 border-primary text-primary"
                          id="stakes-tab"
                          data-bs-toggle="tab"
                          data-bs-target="#stakes-tab-pane"
                          type="button"
                          role="tab"
                        >
                          <i className="fa fa-layer-group me-1"></i> Trade Stakes ({stakes.length})
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          className="nav-link px-4 py-3 fw-semibold border-0 text-muted"
                          id="withdraws-tab"
                          data-bs-toggle="tab"
                          data-bs-target="#withdraws-tab-pane"
                          type="button"
                          role="tab"
                        >
                          <i className="fa fa-arrow-circle-up me-1"></i> Withdrawals ({withdraws.length})
                        </button>
                      </li>
                    </ul>
                  </div>

                  <div className="card-body p-0">
                    <div className="tab-content">

                      {/* TAB 1: STAKES (TRADE) WITH GPS LOCATION & ROUTE */}
                      <div className="tab-pane fade show active" id="stakes-tab-pane" role="tabpanel">
                        <div className="table-responsive">
                          <table className="table table-hover align-middle mb-0 text-nowrap">
                            <thead className="table-light">
                              <tr>
                                <th>Package & Amount</th>
                                <th>Token Asset</th>
                                <th>Daily Reward</th>
                                <th>Total Credit</th>
                                <th>Age (Days)</th>
                                <th>Date & Hash</th>
                                <th>Status</th>
                                <th className="text-end">GPS & Route</th>
                              </tr>
                            </thead>
                            <tbody>
                              {stakes.length > 0 ? (
                                stakes.map((stake) => {
                                  const stakeGps = parseGpsCoordinates(stake.location);
                                  return (
                                    <tr key={stake.id}>
                                      <td>
                                        <div className="fw-semibold text-dark">{stake.package_name || 'Lock Package'}</div>
                                        <span className="fw-bold text-primary">${stake.stake_amount || 0} {stake.stake_asset}</span>
                                      </td>
                                      <td>
                                        <div className="fw-semibold text-dark">{stake.token_name || 'NMRC'}</div>
                                        <span className="text-muted fs-12">{stake.token_amount} @ ${stake.token_price}</span>
                                      </td>
                                      <td>
                                        <div className="text-success fw-semibold">{stake.daily_reward_percentage}% / day</div>
                                        <span className="text-muted fs-12">${stake.daily_credit_reward} credit</span>
                                      </td>
                                      <td>
                                        <span className="fw-bold text-dark">${stake.total_credit || 0}</span>
                                        <div className="text-muted fs-11">Cap: ${stake.stake_capping_value || 0}</div>
                                      </td>
                                      <td>
                                        <span className="badge bg-light text-dark border">
                                          {stake.stake_age || 0} Days
                                        </span>
                                      </td>
                                      <td>
                                        <div className="fs-12 text-muted">{stake.date_time || 'N/A'}</div>
                                        {stake.txn_hash ? (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-link p-0 font-monospace fs-11 text-primary text-decoration-none"
                                            onClick={() => copyToClipboard(stake.txn_hash, 'Transaction Hash')}
                                            title={stake.txn_hash}
                                          >
                                            {formatShortWallet(stake.txn_hash)} <i className="fa fa-copy"></i>
                                          </button>
                                        ) : (
                                          <span className="text-muted fs-11">Pending TX</span>
                                        )}
                                      </td>
                                      <td>
                                        <span className={`badge ${stake.stake_status === 'active' || stake.status === 'success' ? 'bg-success' : stake.stake_status === 'closed' ? 'bg-secondary' : 'bg-warning'}`}>
                                          {stake.stake_status || stake.status || 'Pending'}
                                        </span>
                                      </td>
                                      <td className="text-end">
                                        {stakeGps ? (
                                          <div className="btn-group btn-group-sm">
                                            {/* Define Route Button */}
                                            <button
                                              type="button"
                                              className="btn btn-outline-danger"
                                              onClick={() => openDefineRoute(stakeGps.lat, stakeGps.lng)}
                                              title="Define route to bring on location"
                                            >
                                              <i className="fa fa-route me-1"></i> Route
                                            </button>
                                            {/* Open Map Modal Button */}
                                            <button
                                              type="button"
                                              className="btn btn-outline-primary"
                                              onClick={() => handleOpenGpsModal(`Trade Stake #${stake.id}`, stake.location, stake.date_time)}
                                              title="View location coordinates on map"
                                            >
                                              <i className="fa fa-map-marker-alt"></i>
                                            </button>
                                          </div>
                                        ) : (
                                          <span className="text-muted fs-12">No GPS</span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan="8" className="text-center py-4 text-muted">
                                    No stake records found for this client.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* TAB 2: WITHDRAWALS WITH GPS LOCATION & ROUTE */}
                      <div className="tab-pane fade" id="withdraws-tab-pane" role="tabpanel">
                        <div className="table-responsive">
                          <table className="table table-hover align-middle mb-0 text-nowrap">
                            <thead className="table-light">
                              <tr>
                                <th>Amount & Asset</th>
                                <th>Fee & Net Paid</th>
                                <th>Remaining Bal</th>
                                <th>Addresses</th>
                                <th>Txn Hash</th>
                                <th>Date & Status</th>
                                <th className="text-end">GPS & Route</th>
                              </tr>
                            </thead>
                            <tbody>
                              {withdraws.length > 0 ? (
                                withdraws.map((w) => {
                                  const wGps = parseGpsCoordinates(w.location);
                                  return (
                                    <tr key={w.id}>
                                      <td>
                                        <div className="fw-bold text-dark">{w.withdraw_amount}</div>
                                        <span className="badge bg-light text-muted border">{w.withdraw_in || 'NMRC'}</span>
                                      </td>
                                      <td>
                                        <div className="text-danger fs-12">Fee: {w.fees_deduction}%</div>
                                        <span className="text-success fw-bold">{w.paid_amount}</span>
                                      </td>
                                      <td>
                                        <span className="fw-semibold text-dark">{w.remain_amount}</span>
                                      </td>
                                      <td>
                                        <div className="fs-12 text-muted" title={w.to_address}>
                                          To: <span className="font-monospace text-dark">{formatShortWallet(w.to_address)}</span>
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-link p-0 text-muted ms-1"
                                            onClick={() => copyToClipboard(w.to_address, 'To Address')}
                                          >
                                            <i className="fa fa-copy fs-11"></i>
                                          </button>
                                        </div>
                                      </td>
                                      <td>
                                        {w.txn_hash ? (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-link p-0 font-monospace fs-11 text-primary text-decoration-none"
                                            onClick={() => copyToClipboard(w.txn_hash, 'Txn Hash')}
                                            title={w.txn_hash}
                                          >
                                            {formatShortWallet(w.txn_hash)} <i className="fa fa-copy"></i>
                                          </button>
                                        ) : (
                                          <span className="text-muted fs-11">Pending TX</span>
                                        )}
                                      </td>
                                      <td>
                                        <div className="fs-12 text-muted">{w.date_time}</div>
                                        <span className={`badge ${w.status === 'success' ? 'bg-success' : 'bg-warning'}`}>
                                          {w.status || 'Pending'}
                                        </span>
                                      </td>
                                      <td className="text-end">
                                        {wGps ? (
                                          <div className="btn-group btn-group-sm">
                                            <button
                                              type="button"
                                              className="btn btn-outline-danger"
                                              onClick={() => openDefineRoute(wGps.lat, wGps.lng)}
                                              title="Define route to bring on location"
                                            >
                                              <i className="fa fa-route me-1"></i> Route
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-outline-primary"
                                              onClick={() => handleOpenGpsModal(`Withdrawal #${w.id}`, w.location, w.date_time)}
                                              title="View map"
                                            >
                                              <i className="fa fa-map-marker-alt"></i>
                                            </button>
                                          </div>
                                        ) : (
                                          <span className="text-muted fs-12">No GPS</span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan="7" className="text-center py-4 text-muted">
                                    No withdrawal records found for this client.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>



                    </div>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>

        {/* MODAL 1: LIVE GPS VIEWER & ROUTE DEFINER */}
        {activeGpsModal.isOpen && (
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content border-0 shadow-lg rounded-3">
                <div className="modal-header bg-light">
                  <div>
                    <h6 className="modal-title fw-bold text-dark d-flex align-items-center gap-2 mb-0">
                      <i className="fa fa-map-marked-alt text-danger"></i>
                      <span>{activeGpsModal.title}</span>
                    </h6>
                    <span className="text-muted fs-12">Live GPS Geolocation & Route Navigation</span>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setActiveGpsModal({ ...activeGpsModal, isOpen: false })}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body p-3">
                  {/* Embedded OpenStreetMap View */}
                  <div className="rounded-3 overflow-hidden border mb-3" style={{ height: '350px' }}>
                    <iframe
                      title="GPS Location Map"
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      scrolling="no"
                      marginHeight="0"
                      marginWidth="0"
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${activeGpsModal.lng - 0.015}%2C${activeGpsModal.lat - 0.015}%2C${activeGpsModal.lng + 0.015}%2C${activeGpsModal.lat + 0.015}&layer=mapnik&marker=${activeGpsModal.lat}%2C${activeGpsModal.lng}`}
                    ></iframe>
                  </div>

                  {/* Coordinates & Meta Details */}
                  <div className="p-3 bg-light rounded-3 mb-2">
                    <div className="row g-2 align-items-center">
                      <div className="col-md-7 col-12">
                        <span className="text-muted fs-11 text-uppercase fw-semibold d-block">Coordinates</span>
                        <div className="d-flex align-items-center gap-2 mt-1">
                          <span className="font-monospace fw-bold fs-14 text-dark">
                            {activeGpsModal.lat}, {activeGpsModal.lng}
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary py-0 px-2"
                            onClick={() => copyToClipboard(`${activeGpsModal.lat}, ${activeGpsModal.lng}`, 'Coordinates')}
                            title="Copy coordinates"
                          >
                            <i className="fa fa-copy"></i>
                          </button>
                        </div>
                      </div>
                      <div className="col-md-5 col-12 text-md-end">
                        <span className="text-muted fs-11 text-uppercase fw-semibold d-block">Recorded Timestamp</span>
                        <span className="text-dark fs-12">{activeGpsModal.timestamp || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer bg-light d-flex justify-content-between">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setActiveGpsModal({ ...activeGpsModal, isOpen: false })}
                  >
                    Close
                  </button>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                      onClick={() => openLocationOnMap(activeGpsModal.lat, activeGpsModal.lng)}
                    >
                      <i className="fa fa-external-link-alt"></i> Open in Google Maps
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger d-flex align-items-center gap-1"
                      onClick={() => openDefineRoute(activeGpsModal.lat, activeGpsModal.lng)}
                    >
                      <i className="fa fa-route"></i> Define Route (Get Directions)
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL 2: EDIT USER DETAILS */}
        <div className="modal fade" id="editUserModal" tabIndex="-1" aria-labelledby="editUserModalLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-3">
              <div className="modal-header bg-light">
                <h6 className="modal-title fw-bold text-dark" id="editUserModalLabel">
                  <i className="fa fa-user-edit text-primary me-2"></i>Edit Client Details
                </h6>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <form onSubmit={handleEditSubmit}>
                <div className="modal-body p-4">
                  <div className="mb-3">
                    <label className="form-label fs-13 fw-semibold">Client Name</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fs-13 fw-semibold">Email Address</label>
                    <input
                      type="email"
                      className="form-control form-control-sm"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fs-13 fw-semibold">Mobile Number</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={editMobile}
                      onChange={(e) => setEditMobile(e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fs-13 fw-semibold">Trade Holding In Days</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={editStakeHold}
                      onChange={(e) => setEditStakeHold(e.target.value)}
                    />
                  </div>
                  <div className="row g-2 mb-3">
                    <div className="col-6">
                      <label className="form-label fs-13 fw-semibold">User Status</label>
                      <select
                        className="form-select form-select-sm"
                        value={editUserStatus}
                        onChange={(e) => setEditUserStatus(e.target.value)}
                      >
                        <option value="active">Active</option>
                        <option value="block">Blocked</option>
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label fs-13 fw-semibold">Withdraw Status</label>
                      <select
                        className="form-select form-select-sm"
                        value={editUserWithdraw}
                        onChange={(e) => setEditUserWithdraw(e.target.value)}
                      >
                        <option value="enable">Enable</option>
                        <option value="disable">Disable</option>
                      </select>
                    </div>
                  </div>
                  <div className="row g-2">
                    <div className="col-6">
                      <label className="form-label fs-13 fw-semibold">Upline Income</label>
                      <select
                        className="form-select form-select-sm"
                        value={editUplineIncome}
                        onChange={(e) => setEditUplineIncome(e.target.value)}
                      >
                        <option value="enable">Enable</option>
                        <option value="disable">Disable</option>
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label fs-13 fw-semibold">Downline Income</label>
                      <select
                        className="form-select form-select-sm"
                        value={editDownlineIncome}
                        onChange={(e) => setEditDownlineIncome(e.target.value)}
                      >
                        <option value="enable">Enable</option>
                        <option value="disable">Disable</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="modal-footer bg-light d-flex justify-content-between">
                  <button type="button" className="btn btn-sm btn-outline-secondary" data-bs-dismiss="modal">
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-sm btn-primary" disabled={editLoading}>
                    {editLoading ? 'Saving...' : 'Confirm Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <ToastContainer />
      </Header>
      <Footer />
    </>
  );
}

export default ClientDetails;
